import styles from './GeofenceTooltip.module.scss';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Tooltip } from 'react-leaflet';
import { useListAssetGroupsQuery } from '@casops/store/apis/assetGroupsApi';
import { useListGeofenceAreasQuery } from '@casops/store/apis/geofenceAreasApi';
import { FenceType, GeofenceProperties } from '@casops/store/apis/geofencesApi';

export interface GeofenceTooltipProps {
  properties?: GeofenceProperties;
}

interface DisplayItem {
  title: string;
  value: string;
}

export function GeofenceTooltip({ properties }: GeofenceTooltipProps) {
  const { t } = useTranslation();

  const { data: allAreas } = useListGeofenceAreasQuery(undefined, {
    refetchOnMountOrArgChange: 120,
  });

  const { data: allAssetGroups } = useListAssetGroupsQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const displayItems = useMemo<DisplayItem[]>(() => {
    if (!properties) {
      return [];
    }

    const { name, fenceType, slope, areas, ags } = properties;
    // Remove any null values
    const areaIds: number[] =
      areas
        ?.split(',')
        ?.map((a: string) => parseInt(a))
        ?.filter(Boolean) ?? [];

    const assetGroupIds: number[] =
      ags
        ?.split(',')
        ?.map((a: string) => parseInt(a))
        ?.filter(Boolean) ?? [];

    const displayAreas =
      allAreas && areaIds.length > 0
        ? allAreas
            .filter(a => areaIds.includes(a.id))
            .map(a => a.name)
            .join(', ')
        : t('None');

    const displayGroups =
      allAssetGroups && assetGroupIds.length > 0
        ? allAssetGroups
            .filter(a => assetGroupIds.includes(a.id))
            .map(a => a.name)
            .join(', ')
        : t('None');

    const fenceTypes: string[] = [];

    if (fenceType === FenceType.Intersection) {
      fenceTypes.push(t('GeofenceTypeV2.Intersection'));
    } else if (fenceType === FenceType.WetRoad) {
      fenceTypes.push(t('GeofenceTypeV2.WetRoad'));
    }

    if (slope === true) {
      fenceTypes.push(t('GeofenceTypeV2.Slope'));
    }

    const sortedFenceTypes =
      fenceTypes.length > 0
        ? fenceTypes.sort((a, b) => a.localeCompare(b)).join(', ')
        : t('FenceType.Standard');

    return [
      { title: t('Name'), value: name },
      { title: t('Types'), value: sortedFenceTypes },
      { title: t('Model.Plural.GeofenceArea_other'), value: displayAreas },
      { title: t('Model.Plural.AssetGroup_other'), value: displayGroups },
    ];
  }, [properties, allAssetGroups, allAreas, t]);

  return (
    <Tooltip className={styles.container} direction="right" sticky>
      {displayItems.map((item, index) => (
        <div key={index} className={styles.text}>
          <b>{item.title}: </b>
          {item.value}
        </div>
      ))}
    </Tooltip>
  );
}

export default GeofenceTooltip;
